import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createReactRenderer } from 'helpers/react-renderer';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import Select from '@lightspeed/cirrus-select';
import { Spinner } from '@lightspeed/flame/Spinner';
import CustomLegend from './CustomLegend';
import CustomTick from './CustomTick';
import CustomTooltip from './CustomTooltip';
import { formatAxisNumber, getChartData } from './ItemChartStore';

const chartColors = ['#7ec7ff', '#f5ad4a'];

const components = {
  BarChart,
  LineChart,
};

export default class ItemChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      chart: Object.keys(props.charts)[0],
      data: [],
      legend: [],
      isLoading: true,
    };

    this.handleChartChange = this.handleChartChange.bind(this);
  }

  componentDidMount() {
    this.handleGetChartData();
  }

  handleChartChange(event) {
    const { value: chart } = event.currentTarget;

    this.setState(
      {
        chart,
      },
      () => {
        this.handleGetChartData();
      }
    );
  }

  handleGetChartData() {
    const { chart } = this.state;

    this.setState({
      isLoading: true,
    });

    getChartData(this.props.charts[chart].url).then(response => {
      this.setState({
        legend: response.legend,
        data: response.data,
        isLoading: false,
      });
    });
  }

  render() {
    const { chart, legend, data, isLoading } = this.state;
    const { className, title, description, charts } = this.props;
    const ChartComponent = components[charts[chart].type];

    return (
      <div className={`${className} cirrus`}>
        <div className="cr-flex cr-width-full cr-justify-between cr-mb-2">
          <div>
            <h2 className="cr-uppercase-title">{title}</h2>
            <p className="cr-gray-300">{description}</p>
          </div>
          <div>
            <Select onChange={this.handleChartChange} value={chart} style={{ minWidth: '200px' }}>
              {Object.keys(charts).map(oneChart => (
                <option key={oneChart} value={oneChart}>
                  {charts[oneChart].title}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="chart__wrapper">
          {isLoading && (
            <div className="chart__loading__wrapper">
              <Spinner size="4rem" color="blue" />
            </div>
          )}
          <ResponsiveContainer minHeight={450} notranslate>
            <ChartComponent
              data={data}
              margin={{
                top: 72,
                right: 24,
                left: 0,
                bottom: 20,
              }}
              maxBarSize={80}
            >
              <XAxis dataKey="name" tickLine={false} tick={<CustomTick />} />
              <YAxis
                type="number"
                axisLine={false}
                tickLine={false}
                yAxisId={0}
                tickFormatter={formatAxisNumber}
              />
              <CartesianGrid strokeDasharray="5 8" vertical={false} stroke="#c4cacc" />
              <Tooltip
                cursor={{ fill: 'black', fillOpacity: 0.05 }}
                isAnimationActive={false}
                content={<CustomTooltip />}
              />
              <Legend
                wrapperStyle={{ top: 0, width: '100%', height: 0 }}
                content={<CustomLegend />}
              />
              {legend.map((name, index) => {
                const attributes = {
                  dataKey: name,
                  key: index,
                  fill: chartColors[index],
                  isAnimationActive: false,
                };
                return charts[chart].type === 'BarChart' ? (
                  <Bar {...attributes} />
                ) : (
                  <Line type="linear" stroke={chartColors[index]} {...attributes} />
                );
              })}
            </ChartComponent>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
}

ItemChart.propTypes = {
  className: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  charts: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['BarChart', 'LineChart']).isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const RenderedComponent = createReactRenderer({
  component: ItemChart,
  selector: '[data-react-root="ItemChart"]',
});

history.onChangeState(
  { name: ['item.views.item', 'item.views.quick_add'], tab: 'graphs' },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
